import { makeAutoObservable } from 'mobx'
import { MilestoneFactory, UserFactory, RoyalRingRecipientFactory } from './../../../shared/factories'
import { getRaceKeyFromType } from '../../../constants/race-to-the-ring.constants'

class RaceToTheRingStore {
  constructor() {
    makeAutoObservable(this)
  }

  // Add your observable properties here
  Contestants = {
    all: [],
    ceo: [],
    leader: [],
    agent: [],
  }
  Racers = {
    all: [],
    ceo: [],
    leader: [],
    agent: [],
  }
  Winners = {
    all: [],
    ceo: [],
    leader: [],
    agent: [],
  }
  Recipients = {
    all: [],
    ceo: [],
    leader: [],
    agent: [],
  }
  RecipientMap = {}
  Users = {}
  isLoading = false

  // Add your actions here
  fetch = async () => {
    this.isLoading = true
    try {
      // TODO: Implement data fetching logic
      const Milestones = await MilestoneFactory.search({
        search: {
          mi_type: [
            'agent_ring_race_award',
            'rick_ring_race_award',
            'rick_ring_race',
            'leader_ring_race_award',
            'leader_ring_race',
            'agent_ring_race',
          ],
        },
        pagination: false,
        order_by: { milestone_id: 'DESC' },
      })

      this.Contestants.all = Milestones
      this.Contestants.agent = Milestones.filter(Milestone => Milestone.get('mi_type').includes('agent'))
      this.Contestants.leader = Milestones.filter(Milestone => Milestone.get('mi_type').includes('leader'))
      this.Contestants.ceo = Milestones.filter(Milestone => Milestone.get('mi_type').includes('rick'))

      this.Winners.all = this.Contestants.all.filter(Contestant => Contestant.get('mi_type').includes('award'))
      const winnerIds = this.Winners.all.map(W => parseInt(W.get('user_id')))
      this.Winners.agent = this.Contestants.agent.filter(Contestant => Contestant.get('mi_type').includes('award'))
      const agentIds = this.Winners.agent.map(W => parseInt(W.get('user_id')))
      this.Winners.leader = this.Contestants.leader.filter(Contestant => Contestant.get('mi_type').includes('award'))
      const leaderIds = this.Winners.leader.map(W => parseInt(W.get('user_id')))
      this.Winners.ceo = this.Contestants.ceo.filter(Contestant => Contestant.get('mi_type').includes('award'))
      const ceoIds = this.Winners.ceo.map(W => parseInt(W.get('user_id')))

      this.Racers.all = this.Contestants.all.filter(
        Contestant => !winnerIds.includes(parseInt(Contestant.get('user_id')))
      )
      this.Racers.agent = this.Contestants.agent.filter(
        Contestant => !agentIds.includes(parseInt(Contestant.get('user_id')))
      )
      this.Racers.leader = this.Contestants.leader.filter(
        Contestant => !leaderIds.includes(parseInt(Contestant.get('user_id')))
      )
      this.Racers.ceo = this.Contestants.ceo.filter(Contestant => !ceoIds.includes(parseInt(Contestant.get('user_id'))))

      await Promise.all([this.populateUsers(this.Contestants.all), this.populateRoyalRings(this.Winners.all)])
    } finally {
      this.isLoading = false
    }

    return true
  }
  populateUsers = async (contestants = []) => {
    const ids = Array.from(
      new Set(
        contestants
          .map(contestant => (contestant?.get ? parseInt(contestant.get('user_id'), 10) : null))
          .filter(id => Number.isInteger(id))
      )
    )

    if (!ids.length) {
      this.Users = {}
      return
    }

    try {
      const result = await UserFactory.search({
        search: { id: ids },
        pagination: { per_page: ids.length },
      })

      const users = (Array.isArray(result?.models) ? result.models : []).reduce((acc, user) => {
        if (typeof user?.id === 'function') acc[user.id()] = user
        return acc
      }, {})

      this.Users = users
    } catch (error) {
      console.error('Failed to load users for Race to the Ring', error)
      this.Users = {}
    }
  }

  populateRoyalRings = async (contestants = []) => {
    const ids = Array.from(
      new Set(
        contestants
          .map(contestant => (contestant?.get ? parseInt(contestant.get('user_id'), 10) : null))
          .filter(id => Number.isInteger(id))
      )
    )

    if (!ids.length) {
      this.Recipients = {
        all: [],
        ceo: [],
        leader: [],
        agent: [],
      }
      this.RecipientMap = {}
      return
    }

    try {
      const result = await RoyalRingRecipientFactory.search({
        search: { user_id: ids },
        pagination: { per_page: ids.length },
      })

      const models = Array.isArray(result?.models) ? result.models : []

      this.Recipients = {
        all: models,
        ceo: models.filter(Rec => Rec.get('ring_type') === 'ceo'),
        leader: models.filter(Rec => Rec.get('ring_type') === 'leader'),
        agent: models.filter(Rec => Rec.get('ring_type') === 'agent'),
      }

      this.RecipientMap = models.reduce((acc, recipient) => {
        if (!recipient?.get) return acc
        const userId = parseInt(recipient.get('user_id'), 10)
        if (!Number.isInteger(userId)) return acc

        const ringType = `${recipient.get('ring_type') ?? ''}`.toLowerCase()
        const raceKey = getRaceKeyFromType(ringType)
        const mapKey = `${userId}-${raceKey}`

        if (!acc[mapKey]) acc[mapKey] = recipient
        if (!acc[userId]) acc[userId] = recipient

        return acc
      }, {})
    } catch (error) {
      console.error('Failed to load Royal Ring recipients', error)
      this.Recipients = {
        all: [],
        ceo: [],
        leader: [],
        agent: [],
      }
      this.RecipientMap = {}
    }
  }

  getUserById = userId => {
    if (!userId) return null
    const normalizedId = parseInt(userId, 10)
    if (!Number.isInteger(normalizedId)) return null
    return this.Users?.[normalizedId] || null
  }

  getRecipientByUserId = (userId, raceKey) => {
    if (!userId) return null
    const normalizedId = parseInt(userId, 10)
    if (!Number.isInteger(normalizedId)) return null

    if (raceKey) {
      const key = `${normalizedId}-${raceKey}`
      if (this.RecipientMap?.[key]) return this.RecipientMap[key]
    }

    return this.RecipientMap?.[normalizedId] || null
  }
}

export default new RaceToTheRingStore()
